import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Search, Clock, CheckCircle2, AlertTriangle, User,
  Building2, Timer, ArrowRight, Filter, Package, Play,
} from 'lucide-react';

/* ═══ MOCK ═══ */
const PAZIENTI = [
  { id: 8, nome: 'De Luca', cognome: 'Giovanni', eta: 81, cf: 'DLCGNN45B15H501G', reparto: 'Pneumologia', diagnosi: 'BPCO riacutizzata — Ossigenoterapia', dataDim: '—', stato: 'bozza', draftStep: 0, draftData: { pazCognome: 'De Luca', pazNome: 'Giovanni', pazCf: 'DLCGNN45B15H501G', pazDataNascita: '15/02/1945', diagnosi: 'BPCO riacutizzata', motivazione: '', urgenza: 'ordinaria', noteCliniche: '' } },
  { id: 1, nome: 'Bianchi', cognome: 'Mario', eta: 74, cf: 'BNCMRA52A15H501K', reparto: 'Neurologia', diagnosi: 'Ictus ischemico — emiparesi dx', dataDim: '20/04/2026', stato: 'preso-in-carico', scadenzaOre: 23, scadenzaMin: 45, note: 'Provider: Coop. Riabilita Roma' },
  { id: 2, nome: 'Mancini', cognome: 'Rosa', eta: 91, cf: 'MNCRSA35B45H501L', reparto: 'Oncologia', diagnosi: 'K polmone stadio IV — cure palliative', dataDim: '21/04/2026', stato: 'valutazione-uvmd', note: 'UVMD convocata per il 20/04' },
  { id: 3, nome: 'Conti', cognome: 'Alberto', eta: 79, cf: 'CNTALB47C15H501P', reparto: 'Ortopedia', diagnosi: 'Frattura pertrocanterica — protesi', dataDim: '22/04/2026', stato: 'attesa-verifica', note: 'Richiesta inviata alla COT Roma 2' },
  { id: 4, nome: 'Greco', cognome: 'Salvatore', eta: 70, cf: 'GRCSLV56H15H501R', reparto: 'Chirurgia', diagnosi: 'Colecistectomia — post-operatorio', dataDim: '24/04/2026', stato: 'attesa-verifica', note: 'In attesa verifica documentale COT' },
  { id: 5, nome: 'Vitale', cognome: 'Teresa', eta: 77, cf: 'VTLTRS49D45H501T', reparto: 'Medicina Interna', diagnosi: 'Polmonite su BPCO', dataDim: '19/04/2026', stato: 'preso-in-carico', scadenzaOre: 4, scadenzaMin: 12, note: 'ADI attivata — CM: Inf. L. Conti' },
  { id: 6, nome: 'Lombardi', cognome: 'Franca', eta: 84, cf: 'LMBFNC42E45H501V', reparto: 'Geriatria', diagnosi: 'Scompenso cardiaco riacutizzato', dataDim: '23/04/2026', stato: 'valutazione-uvmd', note: 'Convocazione UVMD in corso' },
  { id: 7, nome: 'Ricci', cognome: 'Paolo', eta: 68, cf: 'RCCPLA58G15H501Z', reparto: 'Cardiologia', diagnosi: 'IMA STEMI — Angioplastica + 2 stent', dataDim: '25/04/2026', stato: 'attesa-verifica', note: 'Documentazione in fase di completamento' },
];

const STATO_STYLE = {
  'bozza': { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-400', label: 'Bozza' },
  'attesa-verifica': { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-400', label: 'Attesa Verifica' },
  'valutazione-uvmd': { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', label: 'Valutazione UVMD' },
  'preso-in-carico': { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Preso in Carico' },
};

function CountdownTimer({ ore, min }) {
  const [h, setH] = useState(ore);
  const [m, setM] = useState(min);
  useEffect(() => {
    const interval = setInterval(() => {
      setM(prev => {
        if (prev === 0) { setH(hh => Math.max(0, hh - 1)); return 59; }
        return prev - 1;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const isUrgent = h < 6;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold ${isUrgent ? 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400 animate-pulse' : 'bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400'}`} data-testid="countdown-timer">
      <Timer className="w-3.5 h-3.5" />
      {String(h).padStart(2, '0')}h {String(m).padStart(2, '0')}m
    </div>
  );
}

export default function OspedaleDashboard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState('tutti');

  const filtered = PAZIENTI.filter(p => {
    const matchSearch = `${p.nome} ${p.cognome} ${p.cf} ${p.reparto}`.toLowerCase().includes(search.toLowerCase());
    const matchStato = filterStato === 'tutti' || p.stato === filterStato;
    return matchSearch && matchStato;
  });

  const counts = { bozza: PAZIENTI.filter(p => p.stato === 'bozza').length, attesa: PAZIENTI.filter(p => p.stato === 'attesa-verifica').length, uvmd: PAZIENTI.filter(p => p.stato === 'valutazione-uvmd').length, carico: PAZIENTI.filter(p => p.stato === 'preso-in-carico').length };

  return (
    <AppLayout role="ospedale">
      <div className="p-4 md:p-6 max-w-[1500px] mx-auto space-y-6" data-testid="ospedale-control-room">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Control Room</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Gestione Dimissioni Protette — Ospedale San Camillo, Roma</p>
          </div>
          <Button className="gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 h-10" onClick={() => navigate('/ospedale/richiesta')} data-testid="btn-nuova-richiesta">
            <Plus className="w-4 h-4" /> Crea nuova richiesta ADI
          </Button>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-3 gap-4" data-testid="ospedale-kpi">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Attesa Verifica</span>
              <Clock className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-3xl font-bold text-slate-700 dark:text-slate-200">{counts.attesa}</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-200 dark:border-amber-800/40 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Valutazione UVMD</span>
              <User className="w-4 h-4 text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{counts.uvmd}</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl border border-emerald-200 dark:border-emerald-800/40 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Preso in Carico</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{counts.carico}</p>
          </div>
        </div>

        {/* Alert Pacchetto Dimissioni */}
        {PAZIENTI.filter(p => p.stato === 'preso-in-carico').length > 0 && (
          <div className="mt-4 bg-red-50 dark:bg-red-950/20 rounded-2xl border-2 border-red-200 dark:border-red-800/50 p-5" data-testid="alert-pacchetto">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h2 className="text-sm font-bold text-red-800 dark:text-red-300">Pacchetto Dimissioni — Scadenze Imminenti</h2>
              <Badge className="bg-red-200 dark:bg-red-800/40 text-red-700 dark:text-red-300 border-0 text-[9px] font-bold ml-auto">URGENTE</Badge>
            </div>
            <p className="text-[11px] text-red-600/80 dark:text-red-400/80 mb-3">Preparare farmaci alla dimissione e piano terapeutico entro 24h dalla presa in carico.</p>
            <div className="flex flex-col gap-2">
              {PAZIENTI.filter(p => p.stato === 'preso-in-carico').map(p => (
                <div key={p.id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/60 dark:bg-slate-900/40 border border-red-100 dark:border-red-800/30" data-testid={`countdown-row-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-xs font-bold text-red-700 dark:text-red-300">{p.cognome[0]}{p.nome[0]}</div>
                    <div>
                      <p className="text-xs font-semibold text-slate-800 dark:text-white">{p.cognome} {p.nome}</p>
                      <p className="text-[10px] text-slate-500">{p.reparto} — Dim. {p.dataDim}</p>
                    </div>
                  </div>
                  <CountdownTimer ore={p.scadenzaOre} min={p.scadenzaMin} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Cerca paziente, CF o reparto..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-10 rounded-xl text-sm" data-testid="ospedale-search" />
          </div>
          <div className="flex items-center gap-1.5">
            {[{ k: 'tutti', l: `Tutti (${PAZIENTI.length})` }, { k: 'bozza', l: `Bozze (${counts.bozza})` }, { k: 'attesa-verifica', l: `Attesa (${counts.attesa})` }, { k: 'valutazione-uvmd', l: `UVMD (${counts.uvmd})` }, { k: 'preso-in-carico', l: `In Carico (${counts.carico})` }].map(f => (
              <button key={f.k} onClick={() => setFilterStato(f.k)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStato === f.k ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'}`} data-testid={`filter-${f.k}`}>{f.l}</button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="ospedale-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700/60">
                {['Paziente', 'Reparto', 'Diagnosi Dimissione', 'Data Dimissione', 'Stato', 'Scadenza 24h', ''].map((col, i) => (
                  <th key={i} className="px-5 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const ss = STATO_STYLE[p.stato];
                return (
                  <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`row-${p.id}`}>
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">{p.cognome} {p.nome}</p>
                      <p className="text-[10px] text-slate-400">{p.eta} anni &middot; {p.cf}</p>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-600 dark:text-slate-300">{p.reparto}</td>
                    <td className="px-5 py-4 text-xs text-slate-600 dark:text-slate-300 max-w-[200px]">{p.diagnosi}</td>
                    <td className="px-5 py-4 text-xs font-medium text-slate-700 dark:text-slate-200">{p.dataDim}</td>
                    <td className="px-5 py-4"><Badge className={`${ss.bg} ${ss.text} border-0 text-[10px] font-semibold gap-1`}><span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />{ss.label}</Badge></td>
                    <td className="px-5 py-4">{p.stato === 'preso-in-carico' ? <CountdownTimer ore={p.scadenzaOre} min={p.scadenzaMin} /> : <span className="text-[10px] text-slate-400">—</span>}</td>
                    <td className="px-5 py-4">
                      {p.stato === 'bozza' && (
                        <button
                          onClick={() => navigate('/ospedale/richiesta', { state: { draftData: p.draftData, draftStep: p.draftStep } })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors"
                          data-testid={`resume-btn-${p.id}`}
                        >
                          <Play className="w-3 h-3" /> Riprendi compilazione
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
